import { useCallback, useEffect, useMemo, useState } from "react";
import { getAllAirportCodes } from "apis/loadsServices";

interface Airport {
  code: string;
  name: string;
}

export interface AirportOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

const CACHE_KEY = "st_airport_codes_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // refresh the list once a day
// Cap rendered results so a 1-character search doesn't render thousands of rows.
const MAX_RESULTS = 100;

// Shared across hook instances so remounts within a session skip even the
// localStorage read/parse.
let inMemoryAirports: Airport[] | null = null;

function readCachedAirports(): Airport[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.airports) ||
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed.airports;
  } catch {
    return null;
  }
}

function writeCachedAirports(airports: Airport[]) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), airports }),
    );
  } catch {
    // Quota/private-mode failures just mean we refetch next visit.
  }
}

// Rank code-prefix matches first (typing "CO" should surface COK before
// airports that merely contain "co" in the name), then substring matches.
function filterAirports(airports: Airport[], search: string): Airport[] {
  if (!search) return airports;
  const term = search.toLowerCase();
  const codeMatches: Airport[] = [];
  const nameMatches: Airport[] = [];
  for (const airport of airports) {
    if (airport.code.toLowerCase().startsWith(term)) {
      codeMatches.push(airport);
    } else if (
      airport.name.toLowerCase().includes(term) ||
      airport.code.toLowerCase().includes(term)
    ) {
      nameMatches.push(airport);
    }
    if (codeMatches.length >= MAX_RESULTS) break;
  }
  return codeMatches.concat(nameMatches).slice(0, MAX_RESULTS);
}

function toOptions(airports: Airport[], disabledCode: string): AirportOption[] {
  return airports.map((airport) => ({
    value: airport.code,
    label: `${airport.code} - ${airport.name}`,
    isDisabled: airport.code === disabledCode,
  }));
}

// Owns the airport autocomplete data for the From/To fields. The full airport
// list is fetched once (cached in localStorage for a day) and both fields
// filter it locally, so typing never waits on the network.
// `fromLocation`/`toLocation` are passed in so each list can disable the
// option already chosen in the other field.
export function useAirportCodes(fromLocation: string, toLocation: string) {
  const [airports, setAirports] = useState<Airport[]>(
    () => inMemoryAirports ?? readCachedAirports() ?? [],
  );
  const [isLoadingAirports, setIsLoadingAirports] = useState(false);
  const [fromSearch, setFromSearch] = useState("");
  const [toSearch, setToSearch] = useState("");

  useEffect(() => {
    if (inMemoryAirports) return;
    const cached = readCachedAirports();
    if (cached) {
      inMemoryAirports = cached;
      return;
    }

    let cancelled = false;
    setIsLoadingAirports(true);
    getAllAirportCodes()
      .then((response: any) => {
        const data =
          response?.data || (Array.isArray(response) ? response : []);
        const normalized: Airport[] = (Array.isArray(data) ? data : [])
          .map((item: any) => ({
            code: item.departureCode,
            name: item.departureName,
          }))
          .filter((airport: Airport) => airport.code && airport.name);
        if (normalized.length > 0) {
          inMemoryAirports = normalized;
          writeCachedAirports(normalized);
        }
        if (!cancelled) setAirports(normalized);
      })
      .catch((error: unknown) => {
        console.error("Error fetching airport codes:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAirports(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const departureOptions: AirportOption[] = useMemo(
    () => toOptions(filterAirports(airports, fromSearch), toLocation),
    [airports, fromSearch, toLocation],
  );

  const arrivalOptions: AirportOption[] = useMemo(
    () => toOptions(filterAirports(airports, toSearch), fromLocation),
    [airports, toSearch, fromLocation],
  );

  const loadDepartureCodes = useCallback((inputValue: string) => {
    setFromSearch(inputValue.trim());
  }, []);

  const loadArrivalCodes = useCallback((inputValue: string) => {
    setToSearch(inputValue.trim());
  }, []);

  const resetDepartureSearch = useCallback(() => {
    setFromSearch("");
  }, []);
  const resetArrivalSearch = useCallback(() => {
    setToSearch("");
  }, []);

  return {
    departureOptions,
    arrivalOptions,
    isLoadingDepartures: isLoadingAirports,
    isLoadingArrivals: isLoadingAirports,
    loadDepartureCodes,
    loadArrivalCodes,
    resetDepartureSearch,
    resetArrivalSearch,
  };
}
